<template>
  <div class="survey-results-chart-histogram d-flex flex-column">
    <span v-if="isTitleVisible && title" class="font_Montserrat font_size_1000rem v3_color_black_knight1">
      {{ title }}
    </span>
    <span v-if="variableTitle" class="font_Montserrat v3_color_black_knight1 mb-2">
      {{ variableTitle }}
    </span>
    <span v-else class="font_Montserrat font_size_1000rem">&nbsp;</span>
    <div class="survey-results-chart-column flex-grow-1" :class="{'border_1 border_grey' : isBorderVisible}">
      <highcharts :options="chartOptions" />
    </div>
    <template v-if="isStatsVisible && isUser">
      <template v-if="!isNoStudyResults && !isJobQuestionResults">
        <b-row
          v-if="stats.my_value && !isMssResults"
          class="mt-4 font_Montserrat font_size_1000rem v3_color_black_knight"
        >
          <b-col cols="6" class="text-center states-header">
            <span class="states-header-name">My Value:</span>
            <span class="ml-4 v3_color_teal">
            {{ prefix }}
            <span v-if="stats.my_value">
              {{ stats.my_value | formatNumber }}
            </span>
            <span v-else>0</span>
            {{ suffix }}
          </span>
          </b-col>

          <b-col cols="6" class="text-center states-header" v-if="stats.my_value">
            <span class="states-header-name">My Percentile:</span>
            <span class="ml-4 v3_color_teal">
            <span class="fw-bold">
              {{ percentileRank }}
              <sup>{{ percentileSuper }}</sup>
            </span>
          </span>
          </b-col>
        </b-row>
        <b-row
          v-else-if="!isMssResults"
          class="mt-4 font_Montserrat font_size_875rem v3_color_black_knight"
        >
          <b-col cols="6" class="m">
            <span>My Value:</span>
            <span class="ml-4 v3_color_teal">
            {{ prefix }}
            <span v-if="stats.my_value">
              {{ stats.my_value | formatNumber }}
            </span>
            <span v-else>0</span>
            {{ suffix }}
          </span>
          </b-col>
        </b-row>

        <template v-if="isWithReport">
          <b-row>
            <b-col cols="12" sm="12" class="mb-2">
              <table class="stats-table w-100">
                <thead>
                <tr>
                  <th>N</th>
                  <th>Min</th>
                  <th>Max</th>
                  <th>
                    <em>
                      P
                      <sub>25</sub>
                    </em>
                  </th>
                  <th>Median</th>
                  <th>
                    <em>
                      P
                      <sub>75</sub>
                    </em>
                  </th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td>{{ stats.count | formatNumber }}</td>
                  <td>
                    {{ statsPrefix }}{{ stats.min | formatNumber
                    }}{{ statsSuffix }}
                  </td>
                  <td>
                    {{ statsPrefix }}{{ stats.max | formatNumber
                    }}{{ statsSuffix }}
                  </td>
                  <td>
                    {{ statsPrefix }}
                    {{ stats.percentile_25 | formatNumber }}
                    {{ statsSuffix }}
                  </td>
                  <td>
                    {{ statsPrefix }}
                    {{ stats.median | formatNumber }}
                    {{ statsSuffix }}
                  </td>
                  <td>
                    {{ statsPrefix }}
                    {{ stats.percentile_75 | formatNumber }}
                    {{ statsSuffix }}
                  </td>
                </tr>
                </tbody>
              </table>
            </b-col>
          </b-row>
        </template>
        <template v-else>
          <b-row>
            <b-col cols="12" sm="12" lg="6" class="mb-2">
              <table class="stats-table w-100">
                <thead>
                <tr>
                  <th>N</th>
                  <th>Min</th>
                  <th>Max</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td>{{ stats.count | formatNumber }}</td>
                  <td>
                    {{ statsPrefix }}{{ stats.min | formatNumber
                    }}{{ statsSuffix }}
                  </td>
                  <td>
                    {{ statsPrefix }}{{ stats.max | formatNumber
                    }}{{ statsSuffix }}
                  </td>
                </tr>
                </tbody>
              </table>
            </b-col>
            <b-col cols="12" sm="12" lg="6">
              <table class="stats-table w-100">
                <thead>
                <tr>
                  <th>
                    <em>
                      P
                      <sub>25</sub>
                    </em>
                  </th>
                  <th>Median</th>
                  <th>
                    <em>
                      P
                      <sub>75</sub>
                    </em>
                  </th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td>
                    {{ statsPrefix }}
                    {{ stats.percentile_25 | formatNumber }}
                    {{ statsSuffix }}
                  </td>
                  <td>
                    {{ statsPrefix }}
                    {{ stats.median | formatNumber }}
                    {{ statsSuffix }}
                  </td>
                  <td>
                    {{ statsPrefix }}
                    {{ stats.percentile_75 | formatNumber }}
                    {{ statsSuffix }}
                  </td>
                </tr>
                </tbody>
              </table>
            </b-col>
          </b-row>
        </template>

        <div style="clear: both;"></div>
      </template>
      <template v-else>
        <b-row
          class="mt-4 font_Montserrat font_size_875rem v3_color_black_knight"
        >
          <b-col cols="6" class="">
            <span>Median =</span>
            <span class="ml-4 v3_color_teal">
            {{ statsPrefix }}{{ stats.median | formatNumber }}{{ statsSuffix }}
          </span>
          </b-col>
        </b-row>
      </template>

      <div
        v-if="responsesCount"
        class="text-right survey-results__responses-count mb-3"
      >
        <span>N = {{ responsesCount | formatNumber }}</span>
        <span v-if="unknownResponsesCount !== null && unknownResponsesCount !== undefined">,<span class="pl-1">Unknown = {{ unknownResponsesCount | formatNumber }}</span></span>
      </div>
    </template>
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue';
import Highcharts from 'highcharts';
import histogramBellcurve from 'highcharts/modules/histogram-bellcurve';
import Utils from '@/providers/Utils.js';
import { getCharHeightForChartTypeAndWidth } from '@/helpers/chartsHelper';

histogramBellcurve(Highcharts);

export default {
  name: 'SurveyResultsChartHistogram',
  components: {
    highcharts: Chart,
  },
  props: {
    title: String,
    variableTitle: String,
    question: Object,
    responses: Array,
    stats: Object,
    format: String, // 'dashboard' / 'results' / 'filters' / 'no-study-completed'
    withScatter: {
      type: Boolean,
      default: false,
    },
    responsesCount: [Number, String],
    unknownResponsesCount: {
      type: [Number, String],
      default: 0
    },
    isStatsVisible: {
      type: Boolean,
      default: true,
    },
    isTitleVisible: {
      type: Boolean,
      default: true
    },
    isBorderVisible: {
      type: Boolean,
      default: true
    },
    isWithReport: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    const plotBands = [
      {
        from: this.stats.percentile_25,
        to: this.stats.percentile_25,
        color: '#0000ff',
        zIndex: 5,
        label: {
          useHTML: false,
          text: 'P<sub>25</sub>',
          style: {
            fontStyle: 'italic',
            color: '#0000ff',
          },
          align: 'right',
          x: -5,
          y: 30,
          verticalAlign: 'top',
        },
      },
      {
        from: this.stats.percentile_75,
        to: this.stats.percentile_75,
        color: '#0000ff',
        zIndex: 5,
        label: {
          useHTML: false,
          text: 'P<sub>75</sub>',
          align: 'left',
          style: {
            fontStyle: 'italic',
            color: '#0000ff',
          },
          x: 5,
          y: 30,
          verticalAlign: 'top',
        },
      },
      {
        from: this.stats.median,
        to: this.stats.median,
        color: '#aaaaaa',
        zIndex: 5,
        label: {
          useHTML: false,
          text: 'Median',
          align: 'left',
          style: {
            fontStyle: 'italic',
            color: '#aaaaaa',
          },
          x: 5,
          verticalAlign: 'top',
        },
      },
    ];
    if (
      this.format !== 'admin-survey-results' &&
      this.format !== 'no-study-completed' &&
      this.format !== 'job-question' &&
      this.$store.getters.userIsMSSUser
    ) {
      plotBands.push({
        from: this.stats.my_value,
        to: this.stats.my_value,
        color: 'green',
        borderColor: 'green',
        borderWidth: 2,
        zIndex: 5,
        label: {
          useHTML: true,
          align: 'left',
          x: 5,
          verticalAlign: 'top',
        },
      });
    }
    const commonChartOptions = {
      chart: {
        backgroundColor: 'transparent',
        events: {
          render: () => this.$emit('chart-rendered'),
        },
        height: getCharHeightForChartTypeAndWidth(this.type, this.windowWidth),
      },
      title: undefined,
      legend: { enabled: false },
      credits: { enabled: false },

      plotOptions: {
        series: {
          color: '#5AC3D8',
        },
      },
    };
    const onlyHistogramOptions = {
      xAxis: [
        {
          //visible: this.format !== 'no-study-completed',
          visible: true,
          title: { text:  this.question && this.question.x_axis_label ? this.question.x_axis_label : 'Value' },
          plotBands: plotBands,
        },
      ],
      yAxis: [
        {
          title: { text: '' },
          labels: {
            formatter: this.getHistogramLabelFormatter(),
          },
        },
      ],
      tooltip: {
        useHTML: true,
        backgroundColor: '#fff',
        zIndex: 9,
      },
      series: [
        {
          name: 'Histogram',
          type: 'histogram',
          xAxis: 0,
          yAxis: 0,
          baseSeries: 's1',
          binsNumber: 20,
          pointPadding: 0,
          zIndex: -1,
          tooltip: {
            pointFormatter: this.getHistogramTooltipPointFormatter(),
          },
        },
        {
          visible: false,
          data: this.responses,
          id: 's1',
        },
      ],
    };
    const withScatterOptions = {
      xAxis: [
        {
          title: { text: 'Histogram' },
          plotBands: plotBands,
        },
        {
          //visible: false,
          visible: true,
        },
      ],
      yAxis: [
        {
          title: { text: 'Histogram' },
          labels: {
            formatter: this.getHistogramLabelFormatter(),
          },
        },
        {
          title: { text: 'Value' },
          opposite: true,
        },
      ],
      tooltip: {
        useHTML: true,
        backgroundColor: '#fff',
      },
      series: [
        {
          name: 'Histogram',
          type: 'histogram',
          xAxis: 0,
          yAxis: 0,
          baseSeries: 's1',
          binsNumber: 20,
          pointPadding: 0,
          zIndex: -1,
          tooltip: {
            pointFormatter: this.getHistogramTooltipPointFormatter(),
          },
        },
        {
          // NB: this series is invisible, but is required to make the histogram work!
          name: 'Value',
          type: 'scatter',
          data: this.responses,
          xAxis: 1,
          yAxis: 1,
          id: 's1',
          marker: {
            radius: 1.5,
          },
          tooltip: {
            pointFormatter: function () {
              const val = Highcharts.numberFormat(this.y, 0, '.', ',');
              return `${val}`;
            },
          },
        },
      ],
    };
    return {
      percentileRank: null,
      percentileSuper: null,
      chartOptions: {
        ...commonChartOptions,
        ...(this.withScatter ? withScatterOptions : onlyHistogramOptions),
      },
    };
  },
  created: function () {
    this.updatePercentileRank();
    this.updateChartHeight();
  },
  computed: {
    isMssResults: function () {
      return this.format === 'admin-survey-results';
    },
    isNoStudyResults: function () {
      return this.format === 'no-study-completed';
    },
    isJobQuestionResults: function () {
      return this.format === 'job-question';
    },
    prefix: function () {
      if (this.question.firstVariable.prefix) {
        return this.question.firstVariable.prefix;
      } else {
        return '';
      }
    },
    isUser: function() {
     return this.$store.getters.userIsMSSUser ? true : false;
    },
    statsPrefix: function () {
      // only show this if it's short
      return this.prefix.length <= 2 ? this.prefix : '';
    },
    suffix: function () {
      if (this.question.firstVariable.suffix) {
        if (
          parseInt(this.stats.my_value) === 1 &&
          this.question.firstVariable.singular_suffix
        ) {
          return this.question.firstVariable.singular_suffix;
        } else {
          return this.question.firstVariable.suffix;
        }
      } else {
        return '';
      }
    },
    statsSuffix: function () {
      // only show this if it's short
      return this.suffix && this.suffix.length <= 2 ? this.suffix : '';
    },
  },
  methods: {
    numberSuffix: function (d) {
      return Utils.numberSuffix(d);
    },
    updatePercentileRank: function () {
      const myV = parseInt(this.stats.my_value);
      const responses = [...this.responses];
      if (!this.responses.includes(myV)) {
        responses.push(myV);
      }
      const sortedData = responses.sort((a, b) => {
        return parseInt(a) - parseInt(b);
      });
      const myIdx = sortedData.lastIndexOf(myV);
      const myPercentRank = Math.round((myIdx / sortedData.length) * 100);
      this.percentileRank = myPercentRank;
      this.percentileSuper = this.numberSuffix(myPercentRank);
    },
    getHistogramLabelFormatter: function () {
      let responses = this.responses;
      return function () {
        if ((responses || []).length > 0) {
          return (
            Highcharts.numberFormat((this.value / responses.length) * 100, 0) +
            '%'
          );
        } else {
          return '';
        }
      };
    },
    getHistogramTooltipPointFormatter: function () {
      let responses = this.responses;
      return function () {
        const x = Highcharts.numberFormat(this.x, 0, '.', ',');
        const x2 = Highcharts.numberFormat(this.x2, 0, '.', ',');
        const y =
          Highcharts.numberFormat(
            (this.y / (responses || []).length) * 100,
            0
          ) + '%';
        return `${x} - ${x2} : ${y}`;
      };
    },
    updateChartHeight: function () {
      this.chartOptions.chart.height = getCharHeightForChartTypeAndWidth(
        this.format,
        this.windowWidth
      );
    },
  },
  watch: {
    responses: function () {
      // refresh data series
      if (
        this.chartOptions &&
        this.chartOptions.series &&
        this.chartOptions.series[1]
      ) {
        this.chartOptions.series[1].data = this.responses;
      }
      this.updatePercentileRank();
    },
    stats: function () {
      if (this.chartOptions && this.chartOptions.xAxis.plotBands) {
        this.chartOptions.xAxis.plotBands[0].from = this.stats.percentile_25;
        this.chartOptions.xAxis.plotBands[0].to = this.stats.percentile_25;

        this.chartOptions.xAxis.plotBands[1].from = this.stats.percentile_75;
        this.chartOptions.xAxis.plotBands[1].to = this.stats.percentile_75;
      }
    },
    windowWidth: function () {
      this.updateChartHeight();
    },
  },
  filters: {
    formatNumber: function (value) {
      const million = 1000 * 1000;
      const billion = 1000 * million;
      const nf = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });

      if (!value) {
        return '0';
      } else if (value >= billion) {
        return nf.format(value / billion) + 'B';
      } else if (value >= million) {
        return nf.format(value / million) + 'M';
        // } else if (value >= 1000) {
        //     return nf.format(value / 1000) + 'K'
      } else {
        return nf.format(value);
      }
    },
  },
};
</script>

<style lang="scss">
@import '../../../sass/v3';
.my_value__badge {
  padding: 0.4em 0.4em 0.25em 0.4em;
  background-color: #0411cc;
  color: white;
  font-size: initial;
}

.my-value {
  margin-bottom: 10px;
}

.stats {
  display: inline-block;
  font-size: 0.8em;
  border: 1px solid lightgray;
  margin-bottom: 10px;
  border-radius: 5px;
}

.stat-pod {
  display: inline-block;
  float: left;
}

.stat-pod__label {
  background-color: rgba(0, 0, 0, 0.03);
  text-align: center;
  padding: 0.5rem;
  border-bottom: 1px solid lightgray;
  font-weight: bold;
}

.stat-pod__label-percentile {
  color: blue;
}

.stat-pod__value-percentile {
  color: blue;
}

.stat-pod__value {
  text-align: center;
  padding: 0.5rem;
}

.stats td {
  text-align: center;
}

.states-header .states-header-name{
  font-weight: bold;
}
.states-header .v3_color_teal{
  font-weight: bold;
}
.stats-table {
  @extend .font_Montserrat;
  @extend .font_size_875rem;
  th,
  td {
    border: 1px solid $v3_color_grey;
    padding: 1rem;
    text-align: center;
  }
  th {
    font-weight: bold;
    text-align:center;
    background-color: rgba($v3_color_teal, 0.1);
    @extend .v3_color_teal;
  }
}
</style>
